import { Component, effect, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import GeoJSON from 'ol/format/GeoJSON';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import { map } from 'rxjs/operators';
import { AppService } from '../../../../services/app.service';
import { EmpowerService } from '../../../../services/empower.service';
import { MapService } from '../../../../services/map.service';
import { EmpowerData } from '../../../classes/empowerData';
import { BaseUILayer } from '../../../classes/layers/base-ui-layer';
import { LayerHeaderComponent } from '../../layer-header/layer-header.component';

@Component({
  selector: 'tgrid-hhs-empower-layer',
  imports: [
    FormsModule,
    LayerHeaderComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './hhs-empower-layer.component.html',
  standalone: true,
  styleUrl: './hhs-empower-layer.component.scss',
})
export class HhsEmpowerLayerComponent extends BaseUILayer {
  private appService = inject(AppService);
  private empowerService = inject(EmpowerService);

  constructor() {
    super();
    effect(() => {
      this.appService.selectedStates();
      if (this.checked) {
        this.getLayerData();
      }
    });
  }

  onDestroy(): void {
  }

  getLayerData() {
    const states = this.appService.selectedStates();

    this.empowerService.getEmpowerData(states).pipe(
      map(res => {
        return {
          type: 'FeatureCollection',
          features: (res.features as {properties: Record<string, unknown>}[]).map((f) => {
            f.properties['popoverData'] = this.mapLayer().createPopover(f.properties);
            return f;
          }),
        };
      }),
    )
      .subscribe(
        featureCollection => {
          const features = new GeoJSON().readFeatures(featureCollection);

          if (this.mapLayer().renderedLayer && this.mapService.isLayerOnMap(this.mapLayer().renderedLayer)) {
            (this.mapLayer().renderedLayer as VectorLayer<VectorSource>).getSource()?.clear();
            (this.mapLayer().renderedLayer as VectorLayer<VectorSource>).getSource()?.addFeatures(features as Feature<Geometry>[]);
            return;
          }

          this.mapLayer().renderedLayer = new VectorLayer({
            source: new VectorSource({
              features: features as Feature<Geometry>[],
              attributions: this.mapLayer().buildAttribution(),
            }),
            properties: {
              name: this.mapLayer().displayName,
            },
            opacity: 0.8,
            style: (feature) => {
              const properties: EmpowerData = feature.getProperties() as EmpowerData;

              const color = (props: EmpowerData) => {
                if (props.beneficiaries === undefined) return '#444';
                if (0 <= props.beneficiaries && props.beneficiaries < 255) return '#C9BC87';
                if (256 <= props.beneficiaries && props.beneficiaries < 976) return '#A69165';
                if (977 <= props.beneficiaries && props.beneficiaries < 7571) return '#856B49';
                if (7572 <= props.beneficiaries) return '#664830';
                return '#000000';
              };

              return new Style({
                stroke: new Stroke({
                  color: 'black',
                  width: 1,
                }),
                fill: new Fill({
                  color: color(properties),
                }),
              });
            },
          });
          MapService.getMapRef()!.addLayer(this.mapLayer().renderedLayer);
        });
  }

  override toggleLayer(checked: boolean): void {
    this.checked = checked;
    if (this.checked) {
      // get hhs empower data
      this.getLayerData();
    } else {
      MapService.getMapRef()!.removeLayer(this.mapLayer().renderedLayer);
    }
  }

  override changeOpacity(opacity: number) {
    this.opacity = opacity;
    (this.mapLayer().renderedLayer as VectorLayer<VectorSource>).setOpacity(this.opacity / 100);
  }

  public exportDataAsCsv(event: MouseEvent): void {
    event.stopPropagation();
    this.mapLayer().downloadFeaturesAsCsv();
  }

}
